import { capacityPerformanceEnumsAffectedRedundancyLevelOptionEnum } from '@/gen/types/CapacityPerformanceEnumsAffectedRedundancyLevelOption';
import { capacityPerformanceEnumsCapacityAndPerformanceCurrentStatusOptionEnum } from '@/gen/types/CapacityPerformanceEnumsCapacityAndPerformanceCurrentStatusOption';
import { capacityPerformanceEnumsCapacityAndPerformanceFaultTypeOptionEnum } from '@/gen/types/CapacityPerformanceEnumsCapacityAndPerformanceFaultTypeOption';
import { capacityPerformanceEnumsCurrentStatusOptionEnum } from '@/gen/types/CapacityPerformanceEnumsCurrentStatusOption';
import { capacityPerformanceEnumsPotentialConsequenceOptionEnum } from '@/gen/types/CapacityPerformanceEnumsPotentialConsequenceOption';

import { whenPathIncludes } from './conditions';
import type { IncidentFieldRule } from './types';

const currentStatusIncludesOther = (values: Record<string, unknown>) => {
    return (
        whenPathIncludes('currentStatus', capacityPerformanceEnumsCurrentStatusOptionEnum.Other)(values) ||
        whenPathIncludes(
            'capacityAndPerformanceCurrentStatus',
            capacityPerformanceEnumsCapacityAndPerformanceCurrentStatusOptionEnum.Other,
        )(values)
    );
};

export const capacityPerformanceFieldRules: IncidentFieldRule[] = [
    {
        key: 'subCategory',
        label: 'Type of capacity or performance issue',
        description: 'Select the type of capacity or efficiency issue observed',
        order: 90,
    },
    {
        key: 'saturatedResource',
        label: 'What resource was saturated?',
        description: 'Select the resource at or near capacity, e.g. power, cooling, space.',
        order: 100,
    },
    {
        key: 'powerAssetID',
        label: 'Asset ID',
        description: 'Enter the asset ID (e.g., UPS-02, GEN-01).',
        order: 110,
    },
    {
        key: 'powerLocation',
        label: 'Where is the affected asset located?',
        description: 'Enter the data hall number, rack, or zone. Be as specific as possible.',
        order: 120,
    },
    {
        key: 'potentialConsequence',
        label: 'Potential operational consequences',
        description: 'Select all potential consequences, e.g. downtime, capacity loss.',
        order: 130,
    },
    {
        key: 'potentialConsequenceExplanation',
        label: 'Potential consequence explanation',
        kind: 'textarea',
        order: 140,
        visibleWhen: whenPathIncludes('potentialConsequence', capacityPerformanceEnumsPotentialConsequenceOptionEnum.Other),
        requiredWhen: whenPathIncludes('potentialConsequence', capacityPerformanceEnumsPotentialConsequenceOptionEnum.Other),
    },
    {
        key: 'capacityAndPerformanceCurrentStatus',
        label: 'Capacity and performance current status',
        order: 150,
    },
    {
        key: 'currentStatus',
        label: 'Current status of the asset',
        description: 'Select the current operational state of the affected asset.',
        order: 160,
    },
    {
        key: 'currentStatusExplanation',
        label: 'Current status explanation',
        kind: 'textarea',
        order: 170,
        visibleWhen: currentStatusIncludesOther,
        requiredWhen: currentStatusIncludesOther,
    },
    {
        key: 'affectedRedundancyLevel',
        label: 'Affected redundancy level',
        description: 'Select the redundancy level impacted, e.g. N, N+1, 2N.',
        order: 180,
    },
    {
        key: 'affectedRedundancyLevelExplanation',
        label: 'Affected redundancy level explanation',
        kind: 'textarea',
        order: 190,
        visibleWhen: whenPathIncludes(
            'affectedRedundancyLevel',
            capacityPerformanceEnumsAffectedRedundancyLevelOptionEnum.Other,
        ),
        requiredWhen: whenPathIncludes(
            'affectedRedundancyLevel',
            capacityPerformanceEnumsAffectedRedundancyLevelOptionEnum.Other,
        ),
    },
    {
        key: 'capacityAndPerformanceFaultType',
        label: 'What type of fault occurred?',
        description: 'Select the type of fault, e.g. mechanical, electrical, software.',
        order: 200,
    },
    {
        key: 'faultTypeExplanation',
        label: 'Fault type explanation',
        kind: 'textarea',
        order: 210,
        visibleWhen: whenPathIncludes(
            'capacityAndPerformanceFaultType',
            capacityPerformanceEnumsCapacityAndPerformanceFaultTypeOptionEnum.Other,
        ),
        requiredWhen: whenPathIncludes(
            'capacityAndPerformanceFaultType',
            capacityPerformanceEnumsCapacityAndPerformanceFaultTypeOptionEnum.Other,
        ),
    },
    {
        key: 'alarmCodeSource',
        label: 'Source of the alarm or alert',
        description: 'Enter the system or tool that generated the alarm, e.g. BMS, DCIM.',
        order: 220,
    },
    {
        key: 'unplannedEmission',
        label: 'Did this cause unplanned CO2 emissions?',
        description: 'Select Yes if the incident caused unexpected CO2 emissions.',
        order: 230,
    },
];
